import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import quote from "svg/quote.svg";
import AccountLookup from "common/accountLookup";
import StoreLookup from "common/storeLookup";
import InputField from "common/customInputField";
import { prcService, salesRepService, getJobDetailsService } from "services/priceQuote.service";
import { address, nationalParent } from "./utils";
import { createQuoteSubmit } from "store/priceQuote/actionCreator";
import { clearPrcList } from "store/slices/priceQuote";
import Button from "react-bootstrap/Button";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { formatPhone } from "utils/common";
import debounce from "lodash/debounce";
import { api } from "../../store/slices/api";
import { toast } from "react-toastify";

const initialQuoteFields = {
  accountName: "",
  contactName: "",
  city: "",
  customerName: "",
  expirationDate: "Upon 30 days notice",
  execName: "",
  execNumber: "",
  phoneNumber: "",
  parentNumber: "",
  parentName: "",
  taxRate: "0.00",
  isTaxChanged: false,
  quoteType: "",
  prcNumber: "",
  prcDesc: "",
  jobNumber: "",
  jobName: "",
  salesRep: "",
  storeName: "",
  storeNumber: "",
  storePhoneNumber: "",
  state: "",
  street: "",
  territory: "",
  quoteName: "",
  quoteId: "",
  zip: "",
};

const QuoteCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.loggedInUser);

  const [isValidStoreLookup, setIsValidStoreLookup] = useState(true);
  const [quoteNameErrMsg, setQuoteNameErrMsg] = useState("");
  const [dateErrMsg, setDateErrMsg] = useState("");
  const [terrErrMsg, setTerrErrMsg] = useState("");
  const [dateType, setDateType] = useState("default");
  const [defaultStoreData, setDefaultStoreData] = useState({});
  const [defaultPrcObj, setDefaultPrcObj] = useState();
  const [defaultJobObj, setDefaultJobObj] = useState();
  const [accChange, setAccChange] = useState(false);
  const [accountValid, setAccountValid] = useState(true);
  // TODO all properties of quoteFields should be separate state variables
  const [quoteFields, setQuoteFields] = useState({ ...initialQuoteFields, territory: currentUser.terrCcn });
  const [accountNumber, setAccountNumber] = useState("");

  const { data: storeInfo } = api.useGetStoreInfoQuery({ storeNumber: currentUser.storeCcn });
  useEffect(() => {
    if (storeInfo !== undefined && storeInfo.length) {
      setDefaultStoreData(storeInfo[0]);
      setQuoteFields({ ...quoteFields, ...storeInfo[0] });
    }
  }, [storeInfo]);

  useEffect(() => {
    if (accChange && accountNumber && accountNumber.length === 9) {
      salesRepService(accountNumber, "1")
        .then((salesRes) => {
          if (salesRes && salesRes.terrCcn === "800100") {
            setQuoteFields({
              ...quoteFields,
              salesRep: `${currentUser.firstname ?? ""} ${currentUser.lastname}`.trim(),
            });
          } else if (salesRes) {
            setQuoteFields({ ...quoteFields, salesRep: `${salesRes.firstName ?? ""} ${salesRes.lastName}`.trim() });
          } else {
            toast.error("Sales rep name could not be found");
          }
        })
        .catch(() => toast.error("Sales rep name could not be found"));

      getJobDetailsService(accountNumber)
        .then((result) => {
          if (result.length) {
            var defaultJobNumber = {};
            if (result.length > 1) {
              defaultJobNumber = result.reduce((previous, current) => {
                return current.jobNbr < previous.jobNbr ? current : previous;
              });
            } else {
              defaultJobNumber = result[0];
            }
            setDefaultJobObj(defaultJobNumber);
            prcService({
              acctNbr: accountNumber,
              jobNbr: defaultJobNumber.jobNbr,
            })
              .then((res) => {
                if (res.prcList.length > 1) {
                  const lowestPrc = res.prcList.reduce((previous, current) => {
                    return current.prcNbr < previous.prcNbr ? current : previous;
                  });
                  setDefaultPrcObj(lowestPrc);
                } else if (res.prcList.length === 1) {
                  setDefaultPrcObj(res.prcList[0]);
                } else {
                  setDefaultPrcObj({ jobNbr: "", jobName: "", prcNbr: "", descr: "" });
                }
              })
              .catch((err) => {
                console.error(err);
                setDefaultPrcObj({ jobNbr: "", jobName: "", prcNbr: "", descr: "" });
              });
          } else {
            setDefaultPrcObj({ jobNbr: "", jobName: "", prcNbr: "", descr: "" });
            setDefaultJobObj({ custId: "", jobName: "", jobNbr: "", terrCcn: "" });
          }
        })
        .catch((err) => {
          console.error(err);
          setDefaultPrcObj({ jobNbr: "", jobName: "", prcNbr: "", descr: "" });
          setDefaultJobObj({ custId: "", jobName: "", jobNbr: "", terrCcn: "" });
        });
    }
  }, [accChange, accountNumber]);

  const onStoreLookupValidation = (isValid) => {
    if (isValid !== isValidStoreLookup) {
      setIsValidStoreLookup(isValid);
    }
  };

  const onAccountNumberChange = (accountNumber, isValid) => {
    setAccountValid(isValid);
    setAccChange(true);
    setAccountNumber(accountNumber);
  };

  const onAccountNameChange = (_accName, _isValid, account) => {
    if (account !== null) {
      const { accountNumber, ...data } = account;
      setAccChange(true);
      setAccountNumber(accountNumber);
      setQuoteFields({ ...quoteFields, ...data });
    }
  };

  const onStoreNumberChange = (storeNumber, data) => {
    setQuoteFields({ ...quoteFields, storeNumber, storePhoneNumber: data.storePhoneNumber });
  };

  const onStoreNameChange = (storeName, isValid, data) => {
    setQuoteFields({ ...quoteFields, storeName, storePhoneNumber: data.storePhoneNumber });
  };

  const handleDateType = (e) => {
    const value = e.target.value;
    if (value === "default") {
      setDateType("default");

      setQuoteFields({ ...quoteFields, expirationDate: "Upon 30 days notice" });
    } else if (value === "manual") {
      setDateType("manual");
      setQuoteFields({ ...quoteFields, expirationDate: "" });
    }
  };

  const validates = () => {
    const { quoteName, expirationDate, territory } = quoteFields;

    if (!quoteName || quoteName == "") {
      setQuoteNameErrMsg("Please enter a name");
      return false;
    } else if (quoteName && quoteName.length > 40) {
      setQuoteNameErrMsg("Please enter maximum of 40 letters");
      return false;
    } else if (!expirationDate || expirationDate == "") {
      setDateErrMsg("Please enter a date");
      return false;
    } else if (expirationDate && new Date(expirationDate) < new Date()) {
      setDateErrMsg("Cannot enter a past date");
      return false;
    } else if (!territory || territory == "") {
      setTerrErrMsg("Please enter Territory Number");
      return false;
    } else if (territory && territory.length < 6) {
      setTerrErrMsg("Please enter 6 digits");
      return false;
    } else if (![0, 9, undefined].includes(accountNumber.length)) {
      return false;
    }

    return true;
  };

  const handleCreate = () => {
    if (!validates() || !isValidStoreLookup) {
      return;
    }

    let tempObj = {
      userName: currentUser.sub,
      territory: quoteFields.territory,
      quoteName: quoteFields.quoteName,
      expirationDate: quoteFields.expirationDate,
      salesRep: quoteFields.salesRep,
      accountNumber,
      accountName: quoteFields.accountName,
      contactName: quoteFields.contactName ?? "",
      customerName: quoteFields.customerName ?? "",
      phoneNumber: quoteFields.phoneNumber ?? "",
      parentNumber: quoteFields.parentNumber ?? "",
      parentName: quoteFields.parentName ?? "",
      execName: quoteFields.execName ?? "",
      execNumber: quoteFields.execNumber ?? "",
      storeNumber: quoteFields.storeNumber,
      storeName: quoteFields.storeName,
      storePhoneNumber: quoteFields.storePhoneNumber ?? "",
      quoteType: null,
      city: quoteFields.city ?? "",
      street: quoteFields.street ?? "",
      state: quoteFields.state ?? "",
      zip: quoteFields.zip ?? "",
      jobNumber: defaultJobObj?.jobNbr ?? "",
      jobName: defaultJobObj?.jobName ?? "",
      prcNumber: defaultPrcObj?.prcNbr ?? "",
      prcDesc: defaultPrcObj?.descr ?? "",
    };
    toast
      .promise(dispatch(createQuoteSubmit(tempObj)), {
        pending: "Creating new quote",
        success: "New price quote created successfully",
        error: "Failed to create a quote, Please try again",
      })
      .then((res) => navigate(`/priceQuote/${res.quoteId}#customer-details`));
    dispatch(clearPrcList());
  };

  const validateExpirationDate = debounce((expirationDate) => {
    if (!expirationDate || expirationDate == "") {
      setDateErrMsg("Please enter a date");
    } else if (expirationDate && new Date(expirationDate) < new Date()) {
      setDateErrMsg("Cannot enter a past date");
    } else {
      setDateErrMsg("");
    }
  }, 2000);

  return (
    <div className="container-fluid search-form-container">
      <div className="search-form-container__title">
        <span className="search-form-container__title--text">
          <img src={quote} alt="" />
          &nbsp;Create a new Quote
        </span>
      </div>
      <div className="search-form-container__fields">
        <form className="search-form-container__fields__form">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Quote Name"
                id="quoteName"
                placeholder="Enter a quote name"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={quoteFields.quoteName}
                onChange={(e) => {
                  const quoteName = e.target.value;

                  setQuoteFields({ ...quoteFields, quoteName });
                  if (!quoteName || quoteName === "") {
                    setQuoteNameErrMsg("Please enter a name");
                  } else if (quoteName && quoteName.length > 40) {
                    setQuoteNameErrMsg("Please enter maximum of 40 letters");
                  } else {
                    setQuoteNameErrMsg("");
                  }
                }}
                isRequired={false}
                errorMsg={quoteNameErrMsg}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Sales Representative"
                placeholder="Enter a name"
                id="salesRep"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={quoteFields.salesRep}
                onChange={(e) => {
                  setQuoteFields({ ...quoteFields, salesRep: e.target.value });
                }}
                isRequired={false}
              />
            </div>
          </div>

          <div className="col-12 form-group account-info__container">
            <p className="account-info__instruction px-0">Select an option for Date</p>
          </div>
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-3 search-form-container__fields__form-group">
              <input
                type="radio"
                name="type"
                value="default"
                onChange={handleDateType}
                className="radioBtn"
                checked={dateType === "default"}
              />
              <label htmlFor="default" className="radioBtntxt">
                Default Date
              </label>
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-2 search-form-container__fields__form-group">
              <input type="radio" name="type" value="manual" onChange={handleDateType} className="radioBtn" />
              <label htmlFor="manual" className="radioBtntxt">
                Select a Date
              </label>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
              {dateType === "default" ? (
                <InputField
                  type="text"
                  label="Expiration Date"
                  id="expirationDate"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={quoteFields.expirationDate}
                  disabled={true}
                />
              ) : (
                <InputField
                  type="date"
                  label="Expiration Date"
                  id="expirationDate"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={quoteFields.expirationDate}
                  onChange={(e) => {
                    const expirationDate = e.target.value;

                    setQuoteFields({ ...quoteFields, expirationDate: e.target.value });
                    validateExpirationDate(expirationDate);
                  }}
                  errorMsg={dateErrMsg}
                />
              )}
            </div>
          </div>
          <AccountLookup
            numberFieldChangeCallback={onAccountNumberChange}
            nameFieldChangeCallback={onAccountNameChange}
          />
          {(quoteFields.customerName ||
            quoteFields.contactName ||
            quoteFields.phoneNumber ||
            quoteFields.nationalParent) && (
            <div className="d-md-flex flex-column flex-md-row mt-5 mt-md-0 overflow-auto position-relative">
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-2">
                <div className="info-item__label">Customer Name</div>
                <div className="info-item__value text-end text-md-start">{quoteFields.customerName}</div>
              </div>
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-2">
                <div className="info-item__label">Contact Name</div>
                <div className="info-item__value text-end text-md-start">{quoteFields.contactName}</div>
              </div>
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
                <div className="info-item__label">Address</div>
                <div className="info-item__value text-end text-md-start">
                  {address(quoteFields.street, quoteFields.city, quoteFields.state, quoteFields.zip)}
                </div>
              </div>
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-2">
                <div className="info-item__label">Phone</div>
                <div className="info-item__value text-end text-md-start">
                  {!quoteFields.phoneNumber ? "N/A" : formatPhone(quoteFields.phoneNumber)}
                </div>
              </div>
              <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-2">
                <div className="info-item__label">National Parent</div>
                <div className="info-item__value text-end text-md-start">
                  {nationalParent(
                    quoteFields.parentNumber,
                    quoteFields.parentName,
                    quoteFields.execNumber,
                    quoteFields.execName,
                  )}
                </div>
              </div>
            </div>
          )}
          <StoreLookup
            mobileView={false}
            numberFieldChangeCallback={onStoreNumberChange}
            nameFieldChangeCallback={onStoreNameChange}
            onStoreLookupValidation={onStoreLookupValidation}
            debounceValue={1000}
            defaultData={defaultStoreData}
          />
          <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
            <InputField
              type="text"
              inputMode="numeric"
              maxLength="6"
              label="Territory number"
              id="territory"
              fieldClasses="form-control search-form-container__fields__form-group__item"
              value={quoteFields.territory}
              onChange={(e) => {
                const territory = e.target.value.replace(/[^0-9a-zA-Z\s]/g, "");
                setQuoteFields({ ...quoteFields, territory });
                if (territory.length === 6) {
                  setTerrErrMsg("");
                }
              }}
              handleOnBlur={(e) => {
                const territory = e.target.value;
                if (!territory || territory === "") {
                  setTerrErrMsg("Please enter Territory Number");
                } else if (territory.length !== 6) {
                  setTerrErrMsg("Please enter 6 digits");
                }
              }}
              isRequired={false}
              errorMsg={terrErrMsg}
            />
          </div>
        </form>
      </div>

      <div className="d-flex justify-content-between flex-column-reverse flex-md-row">
        <Link to="/priceQuote">
          <Button variant="outline-primary" className="mb-5 mb-md-0">
            Back to Quote Search
          </Button>
        </Link>
        <Button className="my-4 my-md-0" onClick={handleCreate} disabled={!accountValid}>
          Create
        </Button>
      </div>
    </div>
  );
};

export default QuoteCreation;
